import { useEffect, useRef, useState, type ReactNode } from "react";
import type { ReviewItem } from "../../shared/review-templates";
import type { ReviewSalesosDTO } from "../../shared/types";
import { Icon } from "../components/Icon";
import { TextLink } from "../components/TextLink";
import { Avatar, Badge, shortDate } from "../components/ui";

// The outreach review page's picture of one message, top to bottom: who it is to, where we are with them, why now,
// and the message itself as it would go out.

export const text = (item: ReviewItem, key: string) => (typeof item[key] === "string" ? (item[key] as string) : "");

export const part = (item: ReviewItem, key: string, name: string) => {
  const value = item[key];
  return value && typeof value === "object" ? (value[name] ?? "") : "";
};

/** The company and the contact, as the record has them right now, over the message they are about to be sent. */
export function Parties({ item, sales, trailing }: { item: ReviewItem; sales: ReviewSalesosDTO | null; trailing?: ReactNode }) {
  const company = sales?.company ?? null;
  const contact = sales?.contact ?? null;
  const companyName = company?.name || part(item, "account", "name") || "This company";
  const contactName = contact?.name || part(item, "contact", "name") || "This contact";
  // The address the message goes to is the approved one, never whatever the record holds now — but a record that
  // has moved on is the first thing a person deciding should see.
  const to = part(item, "contact", "email");
  const moved = contact?.email && to && contact.email.toLowerCase() !== to.toLowerCase() ? contact.email : null;
  const verified = Boolean(contact?.emailVerified) && !moved;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2.5">
        <Avatar name={companyName} src={company?.logoUrl} />
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <h2 className="truncate text-heading font-semibold tracking-tight">{companyName}</h2>
          {company?.status && <Badge tone="zinc">{company.status}</Badge>}
          {company?.doNotContact && <Badge tone="red">Do not contact</Badge>}
        </div>
        {trailing}
      </div>

      <div className="flex items-center gap-2.5">
        <Avatar name={contactName} src={contact?.photoUrl} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="truncate text-sm font-medium">{contactName}</span>
            {contact?.title && <span className="truncate text-sm text-muted">{contact.title}</span>}
            {contact?.doNotContact && <Badge tone="red">Do not contact</Badge>}
          </div>
          <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted">
            <span className="inline-flex min-w-0 items-center gap-1">
              <span className="truncate">{to || "no address"}</span>
              {verified && (
                <span title="SalesOS says this address is valid" className="inline-flex shrink-0 text-success">
                  <Icon name="check" className="size-3.5" />
                </span>
              )}
            </span>
            {contact?.linkedinUrl && (
              <TextLink href={linkedin(contact.linkedinUrl)} quiet>
                LinkedIn
              </TextLink>
            )}
          </div>
          {moved && <p className="mt-1 text-xs text-warning">SalesOS now has {moved} for them.</p>}
        </div>
      </div>
    </div>
  );
}

/** SalesOS keeps a LinkedIn address as a bare path; a link needs the scheme. */
const linkedin = (url: string) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

/** Where we are with this person, in one line: what was sent, what came back. */
export function StateStrip({ sales }: { sales: ReviewSalesosDTO | null }) {
  if (!sales) return <p className="text-sm text-faint">Reading SalesOS…</p>;
  if (!sales.available || !sales.state) return <p className="text-sm text-faint">{sales.unavailable ?? "SalesOS unavailable"}</p>;
  const { sent, lastSentAt, lastReplyAt, replySentiment, bounced } = sales.state;
  const parts = [`${sent} sent`];
  if (!sent) parts.push("never");
  else if (lastSentAt) parts.push(`last ${shortDate(lastSentAt)}`);
  if (lastReplyAt) parts.push(`replied ${shortDate(lastReplyAt)}${replySentiment ? ` (${replySentiment})` : ""}`);
  if (bounced) parts.push("bounced");
  return <p className="text-sm text-muted tabular-nums">{parts.join(" · ")}</p>;
}

/** The agent's reason, two lines of it, with the rest a tap away. */
export function WhyNow({ why }: { why: string }) {
  const [open, setOpen] = useState(false);
  const [clamped, setClamped] = useState(false);
  const ref = useRef<HTMLParagraphElement | null>(null);
  // "More" only when there really is more: a one-line reason must not grow a control that does nothing.
  useEffect(() => {
    const el = ref.current;
    setClamped(Boolean(el && el.scrollHeight > el.clientHeight + 1));
  }, [why]);
  return (
    <div>
      <p ref={ref} className={`text-sm leading-6 text-muted ${open ? "" : "line-clamp-2"}`}>
        <span className="text-faint">Why now · </span>
        {why}
      </p>
      {(clamped || open) && (
        <p className="mt-0.5 text-xs">
          <TextLink onClick={() => setOpen(!open)}>{open ? "less" : "more"}</TextLink>
        </p>
      )}
    </div>
  );
}

/** The message as a person reads it: the door it goes out of, who it is from and to, then the subject and body. */
export function MessageFrame({ item }: { item: ReviewItem }) {
  const contact = `${part(item, "contact", "name")}${part(item, "contact", "email") ? ` · ${part(item, "contact", "email")}` : ""}`;
  const channel = text(item, "channel") || "email";
  const notBefore = text(item, "not_before");
  const expires = text(item, "expires_at");
  return (
    <div className="card overflow-hidden p-0">
      <div className="flex items-center gap-1.5 border-b border-line px-4 py-2 text-xs text-faint md:px-5">
        <Icon name="mail" className="size-3.5" />
        <span className="capitalize">{channel}</span>
      </div>
      <dl className="grid grid-cols-[3.25rem_minmax(0,1fr)] gap-x-3 gap-y-1 bg-fill-subtle px-4 py-3 text-sm md:px-5">
        <dt className="text-faint">From</dt>
        <dd className="min-w-0 truncate text-muted">{part(item, "sender", "email") || "—"}</dd>
        <dt className="text-faint">To</dt>
        <dd className="min-w-0 truncate text-muted">{contact || "—"}</dd>
      </dl>
      <div className="px-4 py-5 md:px-5">
        <h3 className="text-heading font-semibold tracking-tight break-words">{text(item, "subject")}</h3>
        <p className="mt-3 text-reading leading-7 break-words whitespace-pre-wrap text-muted">{text(item, "body")}</p>
      </div>
      {(notBefore || expires) && (
        <div className="flex flex-wrap gap-x-4 border-t border-line px-4 py-2 text-xs text-faint md:px-5">
          {notBefore && <span>Hold until {new Date(notBefore).toLocaleString()}</span>}
          {expires && <span>Drop after {new Date(expires).toLocaleString()}</span>}
        </div>
      )}
    </div>
  );
}
